import { type FC, useCallback, useMemo } from "react";
import type {
  AdminDashboardPoint,
  AdminDashboardStep,
} from "@/admin/entities/adminDashboard";
import {
  buildMoneyChart,
  buildSalesChart,
  buildViewsChart,
  formatCount,
  formatUsdPrecise,
  formatUsdShort,
  isSeriesEmpty,
  revenueShareTooltip,
} from "../model";
import { ComposedDashboardChart } from "./ComposedDashboardChart";
import { DashboardChartCard } from "./DashboardChartCard";

export interface DashboardChartProps {
  series: AdminDashboardPoint[];
  step: AdminDashboardStep;
  loading: boolean;
  error: boolean;
  onRetry: () => void;
}

const CHART_HEIGHT = 320;
const usdTooltip = (value: number | null) =>
  value === null ? "" : formatUsdPrecise(value);

export const SalesRevenueShareChart: FC<DashboardChartProps> = ({
  series,
  step,
  loading,
  error,
  onRetry,
}) => {
  const model = useMemo(() => buildSalesChart(series, step), [series, step]);
  const tooltip = useCallback(
    (value: number | null, index: number, seriesId: string) =>
      seriesId === "revenueShare"
        ? revenueShareTooltip(series, value, index)
        : usdTooltip(value),
    [series]
  );

  return (
    <DashboardChartCard
      title="Продажи и ревшер"
      subheader="Продажи клиентов слева, наш ревшер справа, USD"
      loading={loading}
      error={error}
      onRetry={onRetry}
      empty={isSeriesEmpty(series, ["salesUSD", "revenueShareUSD"])}
      height={CHART_HEIGHT}
    >
      <ComposedDashboardChart
        model={model}
        height={CHART_HEIGHT}
        axisLeft={formatUsdShort}
        axisRight={formatUsdShort}
        tooltip={tooltip}
      />
    </DashboardChartCard>
  );
};

export const MoneyChart: FC<DashboardChartProps> = ({
  series,
  step,
  loading,
  error,
  onRetry,
}) => {
  const model = useMemo(() => buildMoneyChart(series, step), [series, step]);

  return (
    <DashboardChartCard
      title="Деньги"
      subheader="Оплаченные счета, траты и чистый доход, USD"
      loading={loading}
      error={error}
      onRetry={onRetry}
      empty={isSeriesEmpty(series, ["paidInvoicesUSD", "expensesUSD"])}
      height={CHART_HEIGHT}
    >
      <ComposedDashboardChart
        model={model}
        height={CHART_HEIGHT}
        axisLeft={formatUsdShort}
        tooltip={usdTooltip}
      />
    </DashboardChartCard>
  );
};

export const ViewsChart: FC<DashboardChartProps> = ({
  series,
  step,
  loading,
  error,
  onRetry,
}) => {
  const model = useMemo(() => buildViewsChart(series, step), [series, step]);
  const countTooltip = useCallback(
    (value: number | null) => (value === null ? "" : formatCount(value)),
    []
  );

  return (
    <DashboardChartCard
      title="Просмотры"
      subheader="Просмотры и уникальные зрители"
      loading={loading}
      error={error}
      onRetry={onRetry}
      empty={isSeriesEmpty(series, ["views", "viewers"])}
      height={280}
    >
      <ComposedDashboardChart
        model={model}
        height={280}
        axisLeft={formatCount}
        tooltip={countTooltip}
      />
    </DashboardChartCard>
  );
};
